class SessionTokenStorage {
  private key: string;
  private cookieDays: number;
  private inMemoryToken: string | null;

  /**
   * @param key         storage key for localStorage & cookies
   * @param cookieDays  number of days until cookie expiration
   */
  constructor() {
    this.key = "sessionToken";
    this.cookieDays = 7;
    this.inMemoryToken = null;
  }

  private _setCookie(token: string): void {
    const expires = new Date(Date.now() + this.cookieDays * 24 * 60 * 60 * 1_000).toUTCString();

    // Secure flag only works on HTTPS origins
    document.cookie = [`${encodeURIComponent(this.key)}=${encodeURIComponent(token)}`, `expires=${expires}`, "path=/", "Secure", "SameSite=Strict"].join("; ");
  }

  private _getCookie(): string | null {
    const nameEQ = `${encodeURIComponent(this.key)}=`;
    const parts = document.cookie.split(";").map((c) => c.trim());

    for (const part of parts) {
      if (part.startsWith(nameEQ)) {
        return decodeURIComponent(part.substring(nameEQ.length));
      }
    }
    return null;
  }

  /** Expire the cookie immediately */
  private _removeCookie(): void {
    document.cookie = [`${encodeURIComponent(this.key)}=`, "expires=Thu, 01 Jan 1970 00:00:00 GMT", "path=/", "Secure", "SameSite=Strict"].join("; ");
  }

  /**
   * Store the token in memory, cookies & localStorage
   * @param token  the auth token
   */
  public setToken(token: string): void {
    this.inMemoryToken = token;

    // 1) write cookie (secure, samesite=strict)
    this._setCookie(token);

    // 2) write localStorage as secondary persistence
    try {
      window.localStorage.setItem(this.key, token);
    } catch (_) {
      // localStorage may be unavailable or full
    }
  }

  /**
   * Retrieve the token, checking in-memory → cookies → localStorage
   * Caches the result in memory.
   */
  public getToken(): string | null {
    // 1) in‐memory
    if (this.inMemoryToken) {
      console.log("Using in memory token");
      return this.inMemoryToken;
    }

    // 2) secure cookie
    const ck = this._getCookie();
    if (ck) {
      console.log("Using cookie token");
      this.inMemoryToken = ck;
      return ck;
    }

    // 3) localStorage
    try {
      const ls = window.localStorage.getItem(this.key);
      if (ls) {
        console.log("Using localStorage token");
        this.inMemoryToken = ls;
        return ls;
      }
    } catch (_) {
      // ignore
    }

    console.log("No token in memory, cookie or localStorage");
    return null;
  }

  /** Remove the token from memory, cookies & localStorage */
  public removeToken(): void {
    this.inMemoryToken = null;
    this._removeCookie();

    try {
      window.localStorage.removeItem(this.key);
    } catch (_) {
      // ignore
    }
  }
}

export default new SessionTokenStorage();
